import styled from "styled-components";
import {fontFamilies} from "../theme/tokens";

/**
 * Boxed countdown (owner 2026-09-11, from a daisyUI reference): one rounded tile per unit, digits
 * over a full unit word. The tile is a LIGHT TINT of the sender's brand accent — the same
 * `--recipient-brand` variable the branded badge and button set — so a personalized transfer's
 * countdown quietly wears the sender's color, and everything else falls back to the product blue.
 */
export const CountdownRoot = styled.span`
	display: inline-block;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;

	/* Phones: smaller tiles so the recipient card leaves room for the campaign below it at
	   375x812 (owner 2026-09-11) — full-size tiles pushed the card past the campaign-fit
	   threshold and hid the promo on the most common phone viewport. */
	@media (max-width: 480px) {
		font-size: 0.78em;
	}
`;

export const RollingDigits = styled.span`
	display: inline-flex;
	align-items: stretch;
	gap: 0.5em;

	@media (prefers-reduced-motion: reduce) {
		display: none;
	}
`;

/** One tile: digits row above the unit word, both inside the tinted box. */
export const Unit = styled.span`
	display: inline-flex;
	flex-direction: column;
	align-items: center;
	gap: 0.1em;
	padding: 0.5em 0.65em 0.4em;
	border-radius: 0.7em;
	background: ${({theme}) => `color-mix(in srgb, var(--recipient-brand, ${theme.tokens.blue}) 14%, ${theme.tokens.card})`};
	color: ${({theme}) => theme.tokens.ink};
`;

export const UnitDigits = styled.span`
	display: inline-flex;
	font-family: ${fontFamilies.mono};
	font-size: 1.9em;
	font-weight: 700;
	line-height: 1em;
`;

export const UnitLabel = styled.span`
	font-size: 0.72em;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** One clipped row of a vertical digit stack, as in daisyUI's countdown, without its framework. */
export const Digit = styled.span.attrs<{$value: number}>(({$value}) => ({
	style: {"--value": $value} as React.CSSProperties,
}))`
	display: inline-block;
	width: 1ch;
	height: 1em;
	line-height: 1em;
	overflow: clip;

	&::before {
		content: "0\\A 1\\A 2\\A 3\\A 4\\A 5\\A 6\\A 7\\A 8\\A 9";
		display: block;
		white-space: pre;
		text-align: center;
		transform: translateY(calc(var(--value) * -1em));
		transition: transform 1s cubic-bezier(1, 0, 0, 1);
	}

	@media (prefers-reduced-motion: reduce) {
		&::before { transition: none; }
	}
`;

export const PlainCountdown = styled.span`
	display: none;

	@media (prefers-reduced-motion: reduce) {
		display: inline;
	}
`;
